"use client";

import { useEffect, useState, useTransition } from "react";
import { LOCALES, LOCALE_LABELS, type Locale } from "@/lib/locales";
import { CURRENCIES } from "@/lib/currency";
import { setLocaleAction, setCurrencyAction } from "@/lib/locale-actions";

/**
 * Header pill that opens a modal for picking language + currency,
 * Airbnb-style. Tiles auto-apply on click — no submit button, no
 * Enter key. The modal stays on top of the page with a tap-anywhere
 * scrim that closes it.
 *
 * The actual cookie write + redirect happens in src/lib/locale-actions.ts
 * (server actions). The page reloads to the same URL once the cookie
 * is set, so all of /search, /trips, etc. pick up the new locale or
 * currency on the next render.
 */
type Props = {
  currentLocale: Locale;
  currentCurrency: string | null;
};

const LOCALE_FLAGS: Record<Locale, string> = {
  en: "🇬🇧",
  el: "🇬🇷",
  de: "🇩🇪",
  fr: "🇫🇷",
  it: "🇮🇹",
};

const CURRENCY_INFO: Record<string, { symbol: string; name: string; flag: string }> = {
  CHF: { symbol: "CHF", name: "Swiss franc", flag: "🇨🇭" },
  EUR: { symbol: "€", name: "Euro", flag: "🇪🇺" },
  USD: { symbol: "$", name: "US dollar", flag: "🇺🇸" },
  GBP: { symbol: "£", name: "British pound", flag: "🇬🇧" },
};

export default function LocaleCurrencyPicker({
  currentLocale,
  currentCurrency,
}: Props) {
  const [open, setOpen] = useState(false);
  const [, startTransition] = useTransition();
  // Track which value is mid-update so the tile shows a subtle pending
  // state while the server action runs.
  const [pending, setPending] = useState<{ kind: "locale" | "currency"; value: string } | null>(
    null,
  );

  const cur = currentCurrency ?? "EUR";

  // Esc closes the modal; lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  function pickLocale(loc: string) {
    setPending({ kind: "locale", value: loc });
    startTransition(() => {
      setLocaleAction(loc);
    });
  }
  function pickCurrency(c: string) {
    setPending({ kind: "currency", value: c });
    startTransition(() => {
      setCurrencyAction(c);
    });
  }

  return (
    <>
      <button
        type="button"
        className="header-pill"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Language & currency"
      >
        <span>{currentLocale.toUpperCase()}</span>
        <span style={{ color: "var(--ink-4)" }}>·</span>
        <span>{CURRENCY_INFO[cur]?.symbol ?? cur}</span>
      </button>

      {open ? (
        <>
          <button
            type="button"
            aria-label="Close"
            className="lcp-scrim"
            onClick={() => setOpen(false)}
          />
          <div
            className="lcp-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Language and currency"
          >
            <div className="lcp-head">
              <h2 className="lcp-title">Language &amp; currency</h2>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setOpen(false)}
                className="lcp-close"
              >
                ×
              </button>
            </div>

            <section className="lcp-section">
              <h3 className="lcp-section-title">
                Choose a language
                <span className="lcp-section-hint">
                  Applied across the whole site
                </span>
              </h3>
              <div className="lcp-grid">
                {LOCALES.map((loc) => {
                  const active = loc === currentLocale;
                  const isPending =
                    pending?.kind === "locale" && pending.value === loc;
                  return (
                    <button
                      key={loc}
                      type="button"
                      onClick={() => pickLocale(loc)}
                      className={`lcp-tile${active ? " on" : ""}${isPending ? " pending" : ""}`}
                      aria-pressed={active}
                    >
                      <span className="lcp-flag" aria-hidden>
                        {LOCALE_FLAGS[loc]}
                      </span>
                      <span className="lcp-tile-main">{LOCALE_LABELS[loc]}</span>
                      <span className="lcp-tile-sub">{loc.toUpperCase()}</span>
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="lcp-section">
              <h3 className="lcp-section-title">
                Display currency
                <span className="lcp-section-hint">
                  Prices convert at the latest ECB reference rate
                </span>
              </h3>
              <div className="lcp-grid">
                {CURRENCIES.map((c) => {
                  const info = CURRENCY_INFO[c];
                  const active = c === cur;
                  const isPending =
                    pending?.kind === "currency" && pending.value === c;
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => pickCurrency(c)}
                      className={`lcp-tile${active ? " on" : ""}${isPending ? " pending" : ""}`}
                      aria-pressed={active}
                    >
                      <span className="lcp-flag" aria-hidden>
                        {info?.flag ?? "🌍"}
                      </span>
                      <span className="lcp-tile-main">{info?.name ?? c}</span>
                      <span className="lcp-tile-sub">
                        {c} · {info?.symbol ?? ""}
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          </div>
        </>
      ) : null}
    </>
  );
}
